import { Star } from 'lucide-react';
import { useSlideIn } from '../hooks/useSlideIn';
import { reviews } from '../data/reviews';

function StarRating({ rating, small }) {
  var stars = [];
  var sizeClass = small ? "w-4 h-4" : "w-5 h-5";
  for (var i = 0; i < 5; i++) {
    stars.push(
      <Star 
        key={i} 
        className={i < rating ? sizeClass + " fill-yellow-400 text-yellow-400" : sizeClass + " text-gray-300"} 
      />
    );
  }
  return <div className="flex gap-0.5">{stars}</div>;
}

function ReviewCard({ review, index }) {
  var slideIn = useSlideIn({ threshold: 0.1 });
  var direction = index % 2 === 0 ? 'slide-in-left' : 'slide-in-right';
  
  return (
    <div 
      ref={slideIn.ref}
      className={"bg-white p-4 sm:p-6 border border-zinc-200 hover:border-red-200 transition-all duration-300 " + direction + (slideIn.isVisible ? ' visible' : '')}
    >
      <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#E31E24] rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg flex-shrink-0">
          {review.name.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-bold text-neutral-900 text-sm sm:text-base">{review.name}</h4>
            {review.badge ? (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                {review.badge}
              </span>
            ) : null}
          </div>
          <p className="text-xs sm:text-sm text-neutral-500">{review.stats}</p>
        </div>
      </div>
      
      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
        <StarRating rating={review.rating} small={true} />
        <span className="text-xs sm:text-sm text-neutral-400">{review.timeAgo}</span>
      </div>
      
      <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed line-clamp-4">
        {review.text}
      </p>
    </div>
  );
}

export function GoogleReviews() {
  var slideIn = useSlideIn({ threshold: 0.1 });
  
  var reviewCards = [];
  for (var i = 0; i < reviews.length; i++) {
    reviewCards.push(
      <ReviewCard key={reviews[i].id} review={reviews[i]} index={i} />
    );
  }
  
  return (
    <section id="bewertungen" className="py-12 sm:py-20 bg-zinc-50" data-testid="google-reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div 
          ref={slideIn.ref}
          className={"text-center mb-8 sm:mb-12 slide-in-left" + (slideIn.isVisible ? ' visible' : '')}
        >
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <svg className="w-6 h-6 sm:w-8 sm:h-8" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span className="text-base sm:text-lg font-semibold text-neutral-600">Google Bewertungen</span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 mb-3 sm:mb-4">
            Das sagen unsere Kunden
          </h2>
          
          <div className="flex items-center justify-center gap-2 mb-2">
            <StarRating rating={5} />
            <span className="text-xl sm:text-2xl font-bold text-neutral-900">4.9</span>
          </div>
          <p className="text-sm sm:text-base text-neutral-500">Basierend auf Google Rezensionen</p>
        </div>
        
        {/* Reviews Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {reviewCards}
        </div>
        
        {/* Google Link */}
        <div className="text-center mt-8 sm:mt-10">
          <a 
            href="https://share.google/vitLQEyoJq19N1pju" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#E31E24] font-semibold hover:underline text-sm sm:text-base"
            data-testid="google-reviews-link"
          >
            Alle Bewertungen auf Google ansehen
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}

export default GoogleReviews;
